import "../src/styles/custom.css";
import "../src/styles/index.css";
import "../src/styles/index.less";

import { useEffect } from "react";
import { ReactHooksWrapper } from "react-hooks-outside";
import { BrowserRouter, useNavigate } from "react-router-dom";

import { RouteInit } from "./routes";
import { ErrorBoundary } from "@xemplo/error-boundary";

import { ModalProvider, Modal } from "@xemplo/modal";
import { ToastProvider } from "@xemplo/toast";
import { useQueryClientDefaults } from "@xemplo/query-client-defaults";
import { useAuth } from "@xemplo/auth-provider";
import { useCrossAppAuthListener } from "@xemplo/auth-helper";
import { memberActions } from "@xemplo/gp-member-store";
import { useAppSettings } from "@xemplo/xemplo-app-settings";
import { FullscreenLoader } from "./Organization/components/common/Loader/FullscreenLoader";
import { DS_SESSION_ID_STORAGE_KEY } from "./constants/auth";
import { useAppDispatch } from "./hooks";
import { persistor } from "./store";

// The session-state cookies don't work in localhost
// It keeps generating a new cookie and as a result
// the comparision between local store and from api always fails
// and goes in an infinite loop of resetting and detecting new session
const isLocalhost = window.location.hostname === "localhost";

// Checks if the user has logged out and logged back in from a different application
// This is identified based on the differences in the dsSessionId.
const DsSessionIdSync = () => {
  const { dsSessionId } = useAppSettings();
  const { removeUser } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLocalhost || !dsSessionId) return;
    const storedDsSessionId = localStorage.getItem(DS_SESSION_ID_STORAGE_KEY);
    localStorage.setItem(DS_SESSION_ID_STORAGE_KEY, dsSessionId);
    if (!storedDsSessionId || storedDsSessionId === dsSessionId) return;

    dispatch(memberActions.reset());
    // sessionStorage can survive redirect to EIMS if it happens before reset is completed,
    // so an un-flushed reset would have the stale member data rehydrate right
    // back on return, and Login.tsx would then skip refetching the profile
    // entirely (its `needProfileData` check only looks at `details == null`).
    void persistor
      .flush()
      .then(() => removeUser())
      .then(() => navigate("/login", { replace: true }));
  }, [dsSessionId, removeUser, dispatch, navigate]);

  return null;
};

const CrossAppAuthSync = () => {
  useCrossAppAuthListener();
  return null;
};

/**
 * TODO: Convert the Browser Router to RouterProvider
 * The RouterProvider should be initialized within the index.tsx file
 * The App Component should be part of the route definition and init the global stuff
 *
 */
const AppContent = () => {
  useQueryClientDefaults();
  return (
    <BrowserRouter>
      <DsSessionIdSync />
      <CrossAppAuthSync />
      <ModalProvider>
        <FullscreenLoader />
        <Modal />
        <RouteInit />
      </ModalProvider>
      <ErrorBoundary>
        <ReactHooksWrapper />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export const App = () => {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
};

/** @alias */
export default App;
